import { alpha } from '@mui/material/styles';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import Iconify from '../iconify';
import { fDateTime } from '../../utils/formatTime';
import { useClinicalRecordLock } from './ClinicalRecordLockContext';
import { sectionLabel } from './amendmentLabels';

type Props = {
  signedByName?: string | null;
  signedAt?: string | null;
  lastAmendedByName?: string | null;
  lastAmendedAt?: string | null;
};

/**
 * Shown above the clinical tabs: draft / signed & locked / amended state, with
 * "Amend Record" (permission holders only) and the change history, which is
 * visible to everyone who can open the record.
 */
export default function ClinicalRecordStatusBar({
  signedByName,
  signedAt,
  lastAmendedByName,
  lastAmendedAt,
}: Props) {
  const lock = useClinicalRecordLock();
  if (!lock.clinicalRecordId) return null;

  if (lock.isAmending) {
    return (
      <Box
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.warning.main}`,
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
        >
          <Iconify
            icon="eva:edit-2-fill"
            width={24}
            sx={{ color: 'warning.main', flexShrink: 0 }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">
              Amending signed version {lock.currentVersion}. Nothing is saved
              until you sign the amendment.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit a section and click its Save button to add it to the
              amendment. Then click &ldquo;Review &amp; sign&rdquo;, give a
              reason and sign.
            </Typography>
            {!!lock.stagedSections.length && (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1 }}
              >
                {lock.stagedSections.map((s) => (
                  <Chip
                    key={s}
                    size="small"
                    color="warning"
                    variant="soft"
                    label={sectionLabel(s)}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Stack direction="row" spacing={1} flexShrink={0}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={lock.cancelAmend}
            >
              Cancel amendment
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!lock.stagedSections.length}
              onClick={lock.openReview}
              startIcon={<Iconify icon="mdi:draw-pen" />}
            >
              Review &amp; sign
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (!lock.isSigned) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" variant="soft" color="info" label="Draft" />
        <Typography variant="body2" color="text.secondary">
          Editable until signed off. Signing off locks the record.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 1.5,
        border: (theme) =>
          `1px solid ${alpha(lock.isAmended ? theme.palette.warning.main : theme.palette.success.main, 0.4)}`,
        bgcolor: (theme) =>
          alpha(
            lock.isAmended
              ? theme.palette.warning.main
              : theme.palette.success.main,
            0.08,
          ),
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
      >
        <Iconify
          icon="eva:lock-fill"
          width={24}
          sx={{
            color: lock.isAmended ? 'warning.main' : 'success.main',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="subtitle2">
              Signed off by {signedByName || 'Practitioner'}
              {signedAt ? ` on ${fDateTime(signedAt)}` : ''}
            </Typography>
            <Chip size="small" variant="soft" color="success" label="Locked" />
            {/* An unamended record has only its original version, so the
                chip appears once an amendment has created version 2+. */}
            {lock.currentVersion > 1 && (
              <Chip
                size="small"
                variant="soft"
                label={`Version ${lock.currentVersion}`}
              />
            )}
            {lock.isAmended && (
              <Chip
                size="small"
                variant="soft"
                color="warning"
                label="Amended"
              />
            )}
          </Stack>
          {lock.isAmended && (
            <Typography variant="body2" color="text.secondary">
              {`Last amended by ${lastAmendedByName || 'a user'}${
                lastAmendedAt ? ` on ${fDateTime(lastAmendedAt)}` : ''
              }. See what changed, who changed it, when and why.`}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexShrink={0}>
          <Button
            variant={lock.isAmended ? 'contained' : 'outlined'}
            color={lock.isAmended ? 'warning' : 'inherit'}
            onClick={lock.openHistory}
            startIcon={<Iconify icon="eva:clock-outline" />}
          >
            {lock.isAmended ? 'View changes' : 'History'}
          </Button>
          {lock.canAmend && (
            <Button
              variant="outlined"
              color="warning"
              onClick={lock.requestAmend}
              startIcon={<Iconify icon="eva:edit-outline" />}
            >
              Amend Record
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
